import React, { useState } from "react";
import {
  Show,
  TabbedShowLayout,
  Tab,
  TextField,
  BooleanField,
  DateField,
  FunctionField,
  useRecordContext,
  EditButton,
  DeleteButton,
  TopToolbar,
} from "react-admin";
import {
  Chip,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { config } from "../../config";

const RotateSecretButton = () => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleRotate = async () => {
    if (!record?.clientId) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.authServiceUrl}/admin/oauth-clients/${record.clientId}/rotate-secret`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to rotate secret");
      }

      const data = await response.json();
      setNewSecret(data.clientSecret);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (newSecret) {
      navigator.clipboard.writeText(newSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setNewSecret(null);
    setError(null);
  };

  return (
    <>
      <Button
        variant="outlined"
        color="warning"
        startIcon={<VpnKeyIcon />}
        onClick={() => setOpen(true)}
        sx={{ mt: 2 }}
      >
        Ротация Client Secret
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Ротация Client Secret</DialogTitle>
        <DialogContent>
          {!newSecret && !error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Внимание! После ротации старый secret перестанет работать. Все
              приложения, использующие этот client, нужно будет обновить.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {newSecret && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Новый secret сгенерирован! Сохраните его - он больше не будет
                показан.
              </Alert>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography sx={{ flex: 1, fontFamily: "monospace" }}>
                  {newSecret}
                </Typography>
                <Button
                  size="small"
                  onClick={handleCopy}
                  startIcon={<ContentCopyIcon />}
                >
                  {copied ? "Скопировано!" : "Копировать"}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            {newSecret ? "Закрыть" : "Отмена"}
          </Button>
          {!newSecret && (
            <Button
              onClick={handleRotate}
              color="warning"
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Ротировать Secret"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

const OAuthClientShowActions = () => (
  <TopToolbar>
    <EditButton />
    <DeleteButton mutationMode="pessimistic" />
  </TopToolbar>
);

export const OAuthClientShow = () => (
  <Show actions={<OAuthClientShowActions />}>
    <TabbedShowLayout>
      <Tab label="Details">
        <TextField source="clientId" label="Client ID" />
        <TextField source="name" label="Name" />
        <BooleanField source="active" label="Active" />
        <FunctionField
          label="Redirect URIs"
          render={(record: any) => {
            let uris = record.redirectUris;
            if (typeof uris === "string") {
              uris = uris.replace(/[{}]/g, "").split(",");
            }
            if (!Array.isArray(uris)) return null;

            return (
              <Box>
                {uris.map((uri: string) => (
                  <Chip key={uri} label={uri} sx={{ mr: 0.5, mb: 0.5 }} />
                ))}
              </Box>
            );
          }}
        />
        <FunctionField
          label="Allowed Scopes"
          render={(record: any) => {
            let scopes = record.allowedScopes;
            if (typeof scopes === "string") {
              scopes = scopes.replace(/[{}]/g, "").split(",");
            }
            if (!Array.isArray(scopes)) return null;

            return (
              <Box>
                {scopes.map((scope: string) => (
                  <Chip key={scope} label={scope} sx={{ mr: 0.5 }} />
                ))}
              </Box>
            );
          }}
        />
        <DateField source="createdAt" label="Created At" showTime />
        <DateField source="updatedAt" label="Updated At" showTime />

        {/* Rotate Secret Button */}
        <RotateSecretButton />
      </Tab>

      <Tab label="Stats" path="stats">
        <FunctionField
          label="Usage Statistics"
          render={(record: any) => (
            <Box>
              <Typography variant="body1">
                <strong>Total Authorization Codes:</strong>{" "}
                {record.stats?.totalAuthCodes || 0}
              </Typography>
              <Typography variant="body1">
                <strong>Total Tokens Issued:</strong>{" "}
                {record.stats?.totalTokens || 0}
              </Typography>
              <Typography variant="body1">
                <strong>Active Tokens:</strong>{" "}
                {record.stats?.activeTokens || 0}
              </Typography>
            </Box>
          )}
        />
      </Tab>
    </TabbedShowLayout>
  </Show>
);
